import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { projectsApi } from '../../services/projectsApi.js';
import { todayIso } from '../../utils/formatters.js';

function emptyForm() {
  return {
    projectId: '',
    taskId: '',
    employeeId: '',
    entryDate: todayIso(),
    startTime: '',
    endTime: '',
    hours: '',
    description: '',
    billable: true,
    hourlyRate: '0',
  };
}

export function TimeEntryModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);

  const projects = useQuery({
    queryKey: ['projects', 'list', 'for-time'],
    queryFn: () => projectsApi.listProjects({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });
  const tasks = useQuery({
    queryKey: ['projects', form.projectId, 'tasks', 'for-time'],
    queryFn: () => projectsApi.listTasks(form.projectId).then((r) => r.items),
    enabled: open && Boolean(form.projectId),
  });
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'for-time'],
    queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items),
    enabled: open,
  });

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const create = useMutation({
    mutationFn: (body) => projectsApi.createTimeEntry(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'hours-by-project'] });
      setForm(emptyForm());
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const employee = (employees.data || []).find((emp) => emp.id === form.employeeId);
    create.mutate({
      projectId: form.projectId,
      taskId: form.taskId || null,
      employeeId: form.employeeId || null,
      employeeName: employee ? `${employee.firstName} ${employee.lastName}` : null,
      entryDate: form.entryDate,
      startTime: form.startTime || null,
      endTime: form.endTime || null,
      hours: form.hours ? Number(form.hours) : undefined,
      description: form.description || null,
      billable: form.billable,
      hourlyRate: form.hourlyRate || '0',
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('time.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('time.field.project')}</span>
          <select className="select" value={form.projectId} onChange={set('projectId')} required>
            <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
            {(projects.data || []).map((p) => (
              <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('time.field.task')}</span>
          <select className="select" value={form.taskId} onChange={set('taskId')} disabled={!form.projectId}>
            <option value="">{t('common.none')}</option>
            {(tasks.data || []).map((task) => (
              <option key={task.id} value={task.id}>{task.title}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('time.field.employee')}</span>
          <select className="select" value={form.employeeId} onChange={set('employeeId')}>
            <option value="">{t('common.none')}</option>
            {(employees.data || []).map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
            ))}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('time.field.date')}</span>
            <input className="input" type="date" value={form.entryDate} onChange={set('entryDate')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('time.field.hours')}</span>
            <input className="input" type="number" min="0" max="24" step="0.25" value={form.hours} onChange={set('hours')} placeholder={t('time.field.hoursHint')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('time.field.start')}</span>
            <input className="input" type="time" value={form.startTime} onChange={set('startTime')} />
          </label>
          <label className="field">
            <span className="field-label">{t('time.field.end')}</span>
            <input className="input" type="time" value={form.endTime} onChange={set('endTime')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('time.field.rate')}</span>
            <input className="input" type="number" min="0" step="0.01" value={form.hourlyRate} onChange={set('hourlyRate')} />
          </label>
          <label className="field" style={{ justifyContent: 'flex-end' }}>
            <span className="time-billable">
              <input type="checkbox" checked={form.billable} onChange={set('billable')} />
              {t('time.field.billable')}
            </span>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('time.field.description')}</span>
          <textarea className="textarea" rows={2} value={form.description} onChange={set('description')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
